import React, { useEffect, useState, useRef } from "react";
import { Circle } from "../../components/Animation";
import { readDim } from "../../components/services/ReadFile";
import { requestAuth } from "../../components/services/RequestService";
import { validateStatus } from "../../utils/validate";
import { toast } from "react-toastify";

const DIM_FIELD_CONFIG = [
  {
    id: 40,
    initial:
      "https://res.cloudinary.com/dojxoluzj/image/upload/v1695331216/Trazo/Procedure/448/File/2023921_5436.pdf",
    label: "Adjuntar DIM",
    name: "adjuntarDim",
    type: "fileRead",
    unique: false,
    url: "adjuntarDim",
    validate: false,
  },
];

const FormReadDocumentDimWithFiles = () => {
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [items, setItems] = useState([]); // [{ id, status: 'idle'|'loading'|'success'|'error', readStatus: null, message: '' }]
  const [isProcessing, setIsProcessing] = useState(false);
  const [filter, setFilter] = useState("all"); // 'all' | 'idle' | 'success' | 'error'
  const [searchTerm, setSearchTerm] = useState("");

  const isCancelledRef = useRef(false);

  useEffect(() => {
    fetchIds();
  }, []);

  async function fetchIds() {
    setLoadingInitial(true);
    try {
      const res = await requestAuth(
        "get",
        "/Procedure/IdsWithDimNumberAndDimFile"
      );
      const procedureIds = res?.data?.procedureIds || [];
      const formatted = procedureIds.map((id) => ({
        id,
        status: "idle",
        readStatus: null,
        message: "",
      }));
      setItems(formatted);
    } catch (err) {
      console.error(err);
      toast.error("Error al obtener los IDs de trámites con DIM");
    } finally {
      setLoadingInitial(false);
    }
  }

  function readSingleProcedure(procedureId) {
    return new Promise((resolve) => {
      readDim(
        DIM_FIELD_CONFIG,
        0,
        { id: procedureId },
        [],
        (_, __, result) => {
          const resObj = Array.isArray(result) ? result[0] : result;
          const status = resObj?.readStatus;
          const isOk = validateStatus(status);
          const message =
            typeof resObj?.readData === "string"
              ? resObj.readData
              : JSON.stringify(resObj?.readData || "");

          resolve({
            status: isOk ? "success" : "error",
            readStatus: status || (isOk ? 200 : 500),
            message: message || (isOk ? "Lectura completada" : "Error al procesar"),
          });
        }
      );
    });
  }

  async function processQueue(targetIds) {
    if (!targetIds || targetIds.length === 0) return;
    setIsProcessing(true);
    isCancelledRef.current = false;

    // Procesamiento estrictamente uno por uno (secuencial / concurrency: 1)
    const queue = [...targetIds];

    while (queue.length > 0 && !isCancelledRef.current) {
      const id = queue.shift();
      if (!id) continue;

      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, status: "loading" } : it))
      );

      try {
        const outcome = await readSingleProcedure(id);
        setItems((prev) =>
          prev.map((it) =>
            it.id === id
              ? {
                  ...it,
                  status: outcome.status,
                  readStatus: outcome.readStatus,
                  message: outcome.message,
                }
              : it
          )
        );
      } catch (error) {
        setItems((prev) =>
          prev.map((it) =>
            it.id === id
              ? {
                  ...it,
                  status: "error",
                  readStatus: 500,
                  message: error?.message || "Error inesperado",
                }
              : it
          )
        );
      }
    }

    setIsProcessing(false);
  }

  function handleStartAll() {
    const pendingIds = items
      .filter((it) => it.status === "idle")
      .map((it) => it.id);
    if (pendingIds.length === 0) {
      toast.info("No hay trámites pendientes por leer");
      return;
    }
    processQueue(pendingIds);
  }

  function handleRetryFailed() {
    const failedIds = items
      .filter((it) => it.status === "error")
      .map((it) => it.id);
    if (failedIds.length === 0) {
      toast.info("No hay trámites fallidos para reintentar");
      return;
    }
    processQueue(failedIds);
  }

  function handleRetrySingle(id) {
    if (isProcessing) {
      toast.warning("Hay un proceso en marcha. Esperá a que termine o cancelalo.");
      return;
    }
    processQueue([id]);
  }

  function handleStop() {
    isCancelledRef.current = true;
    setIsProcessing(false);
    toast.info("Proceso detenido");
  }

  const total = items.length;
  const countSuccess = items.filter((it) => it.status === "success").length;
  const countError = items.filter((it) => it.status === "error").length;
  const countLoading = items.filter((it) => it.status === "loading").length;
  const countIdle = items.filter((it) => it.status === "idle").length;

  const filteredItems = items.filter((it) => {
    if (filter !== "all" && it.status !== filter) return false;
    if (searchTerm && !String(it.id).includes(searchTerm.trim())) return false;
    return true;
  });

  if (loadingInitial) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <Circle />
        <p className="mt-4 text-gray-600 font-medium">
          Cargando trámites con DIM...
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col gap-4 text-gray-800">
      {/* Header y Resumen */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <i className="fas fa-file-invoice text-blue-600"></i>
            Lectura de Documentos DIM (Uno por uno)
          </h2>
          <p className="text-sm text-gray-500">
            Total detectados con número y archivo DIM:{" "}
            <span className="font-semibold text-gray-700">{total}</span>
          </p>
        </div>

        {/* Acciones principales */}
        <div className="flex items-center gap-2">
          {!isProcessing ? (
            <>
              {countIdle > 0 && (
                <button
                  type="button"
                  onClick={handleStartAll}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow flex items-center gap-2 text-sm transition"
                >
                  <i className="fas fa-play"></i> Leer Pendientes ({countIdle})
                </button>
              )}
              {countError > 0 && (
                <button
                  type="button"
                  onClick={handleRetryFailed}
                  className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-4 py-2 rounded-md shadow flex items-center gap-2 text-sm transition"
                >
                  <i className="fas fa-redo-alt"></i> Reintentar Fallidos (
                  {countError})
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={handleStop}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-md shadow flex items-center gap-2 text-sm transition"
            >
              <i className="fas fa-stop"></i> Detener proceso
            </button>
          )}
        </div>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-sm">
        <div
          onClick={() => setFilter("all")}
          className={`cursor-pointer p-2.5 rounded-lg border transition ${
            filter === "all"
              ? "border-blue-600 bg-blue-50 font-bold"
              : "border-gray-200 bg-gray-50 hover:bg-gray-100"
          }`}
        >
          <div className="text-gray-500 text-xs">Total</div>
          <div className="text-lg font-bold text-gray-800">{total}</div>
        </div>
        <div
          onClick={() => setFilter("idle")}
          className={`cursor-pointer p-2.5 rounded-lg border transition ${
            filter === "idle"
              ? "border-gray-600 bg-gray-100 font-bold"
              : "border-gray-200 bg-gray-50 hover:bg-gray-100"
          }`}
        >
          <div className="text-gray-500 text-xs">Pendientes</div>
          <div className="text-lg font-bold text-gray-600">{countIdle}</div>
        </div>
        <div
          onClick={() => setFilter("success")}
          className={`cursor-pointer p-2.5 rounded-lg border transition ${
            filter === "success"
              ? "border-green-600 bg-green-50 font-bold"
              : "border-green-200 bg-green-50/50 hover:bg-green-100/50"
          }`}
        >
          <div className="text-green-600 text-xs">Exitosos</div>
          <div className="text-lg font-bold text-green-700">{countSuccess}</div>
        </div>
        <div
          onClick={() => setFilter("error")}
          className={`cursor-pointer p-2.5 rounded-lg border transition ${
            filter === "error"
              ? "border-red-600 bg-red-50 font-bold"
              : "border-red-200 bg-red-50/50 hover:bg-red-100/50"
          }`}
        >
          <div className="text-red-600 text-xs">Fallidos</div>
          <div className="text-lg font-bold text-red-700">{countError}</div>
        </div>
      </div>

      {/* Barra de progreso */}
      {total > 0 && (
        <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden flex">
          <div
            className="bg-green-500 h-2.5 transition-all duration-300"
            style={{ width: `${(countSuccess / total) * 100}%` }}
            title={`Exitosos: ${countSuccess}`}
          ></div>
          <div
            className="bg-red-500 h-2.5 transition-all duration-300"
            style={{ width: `${(countError / total) * 100}%` }}
            title={`Fallidos: ${countError}`}
          ></div>
          <div
            className="bg-blue-500 h-2.5 transition-all duration-300 animate-pulse"
            style={{ width: `${(countLoading / total) * 100}%` }}
            title={`Leyendo: ${countLoading}`}
          ></div>
        </div>
      )}

      {/* Buscador */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <i className="fas fa-search absolute left-3 top-3 text-gray-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por ID de trámite..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        {searchTerm && (
          <button
            onClick={() => setSearchTerm("")}
            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
          >
            Limpiar
          </button>
        )}
      </div>

      {/* Lista de trámites */}
      <div className="max-h-[420px] overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100 bg-white">
        {filteredItems.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            No se encontraron trámites con el filtro aplicado.
          </div>
        ) : (
          filteredItems.map((item) => {
            const isItemLoading = item.status === "loading";
            const isSuccess = item.status === "success";
            const isError = item.status === "error";

            return (
              <div
                key={item.id}
                className={`p-3 flex items-center justify-between gap-3 text-sm transition ${
                  isSuccess
                    ? "bg-green-50/30"
                    : isError
                    ? "bg-red-50/30"
                    : isItemLoading
                    ? "bg-blue-50/40"
                    : "hover:bg-gray-50"
                }`}
              >
                {/* Trámite ID e info */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="font-semibold text-gray-800 flex items-center gap-2">
                    <span className="text-gray-400 font-normal">#</span>
                    {item.id}
                  </div>

                  {/* Estado badge */}
                  <div>
                    {item.status === "idle" && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                        Pendiente
                      </span>
                    )}
                    {isItemLoading && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 animate-pulse">
                        <i className="fas fa-spinner fa-spin text-xs"></i> Leyendo...
                      </span>
                    )}
                    {isSuccess && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                        <i className="fas fa-check-circle"></i> Leído (HTTP {item.readStatus})
                      </span>
                    )}
                    {isError && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                        <i className="fas fa-exclamation-triangle"></i> Falló ({item.readStatus || "Error"})
                      </span>
                    )}
                  </div>

                  {/* Mensaje o detalle si hay error o resultado */}
                  {item.message && (
                    <span
                      className={`truncate max-w-xs md:max-w-md text-xs ${
                        isError ? "text-red-600" : "text-gray-500"
                      }`}
                      title={item.message}
                    >
                      {item.message}
                    </span>
                  )}
                </div>

                {/* Acciones individuales */}
                <div>
                  {(isError || item.status === "idle") && (
                    <button
                      type="button"
                      disabled={isProcessing}
                      onClick={() => handleRetrySingle(item.id)}
                      className="px-2.5 py-1 text-xs font-medium border border-gray-300 rounded hover:bg-gray-100 text-gray-700 disabled:opacity-50 transition flex items-center gap-1"
                      title="Procesar / Reintentar este trámite"
                    >
                      <i className="fas fa-redo-alt text-xs"></i>
                      {isError ? "Reintentar" : "Leer"}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FormReadDocumentDimWithFiles;
